import React, { useMemo, useState } from 'react'
import {
  SelectChip,
  SELECT_CHIP_DEMO_OPTIONS,
  SELECT_CHIP_PREVIEW_STATE_LABELS,
  SELECT_CHIP_PREVIEW_STATES,
  type SelectChipPreviewState,
} from '@enamelai/design-system'
import UiPropertiesPanel, {
  type UiPropertySection,
} from '../components/UiPropertiesPanel'
import UiPreviewCanvas from '../components/UiPreviewCanvas'
import './SelectChipShowcase.css'

const SelectChipShowcase: React.FC = () => {
  const [previewState, setPreviewState] =
    useState<SelectChipPreviewState>('open')
  const [label, setLabel] = useState('Title')
  const [value, setValue] = useState<string | null>('item-3')

  const propertySections: UiPropertySection[] = useMemo(
    () => [
      {
        title: 'Appearance',
        fields: [
          {
            id: 'select-chip-state',
            label: 'Highlighted state',
            control: (
              <select
                id="select-chip-state"
                className="ui-field-control"
                value={previewState}
                onChange={(event) =>
                  setPreviewState(event.target.value as SelectChipPreviewState)
                }
              >
                {SELECT_CHIP_PREVIEW_STATES.map((option) => (
                  <option key={option} value={option}>
                    {SELECT_CHIP_PREVIEW_STATE_LABELS[option]}
                  </option>
                ))}
              </select>
            ),
          },
        ],
      },
      {
        title: 'Content',
        fields: [
          {
            id: 'select-chip-label',
            label: 'Label',
            control: (
              <input
                id="select-chip-label"
                className="ui-field-control"
                type="text"
                value={label}
                onChange={(event) => setLabel(event.target.value)}
              />
            ),
          },
        ],
      },
    ],
    [label, previewState],
  )

  return (
    <>
      <UiPreviewCanvas
        title="Select chip"
        description="Compact chip trigger with a single-select dropdown, distinct from the form Select field."
        stageClassName="ui-preview__stage--wide select-chip-showcase__stage"
      >
        <div
          className={['select-chip-showcase__sheet'].filter(Boolean).join(' ')}
        >
          <section className={`ui-showcase-live`}>
            <h3 className="ui-showcase-live__title">Live preview</h3>
            <p className="ui-showcase-live__note">
              Click the chip to open the menu and pick an item.
            </p>
            <div className="select-chip-showcase__live-row">
              <SelectChip
                label={label}
                options={SELECT_CHIP_DEMO_OPTIONS}
                value={value}
                onChange={setValue}
              />
            </div>
            <p className="select-chip-showcase__status">
              Value: {value ?? 'none'}
            </p>
          </section>

          <section className="select-chip-showcase__sample">
            <h3 className="ui-showcase-live__title">States</h3>
            <p className="ui-showcase-live__note">
              Default, Selected, and Open.
            </p>
            <div className="select-chip-showcase__matrix">
              {SELECT_CHIP_PREVIEW_STATES.map((state) => (
                <div
                  key={state}
                  className={[
                    'select-chip-showcase__sample-cell',
                    state === 'open'
                      ? 'select-chip-showcase__sample-cell--open'
                      : '',
                    state === previewState
                      ? 'select-chip-showcase__sample-cell--highlighted'
                      : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                >
                  <h4 className="select-chip-showcase__label">
                    {SELECT_CHIP_PREVIEW_STATE_LABELS[state]}
                  </h4>
                  <SelectChip
                    label={label}
                    previewState={state}
                    options={SELECT_CHIP_DEMO_OPTIONS}
                    value={state === 'default' ? null : 'item-3'}
                  />
                </div>
              ))}
            </div>
          </section>
        </div>
      </UiPreviewCanvas>
      <UiPropertiesPanel
        componentName="SelectChip"
        sections={propertySections}
      />
    </>
  )
}

export default SelectChipShowcase
